import { Box, Typography, MenuItem } from '@mui/material';
import { ChatCircleText, FolderUser } from '@phosphor-icons/react';
import { IFluxBotModel } from '../../../data/models/flux-bot';
import { useState } from 'react';
import { useAuthStore } from '../../../store/storeAuth';
import { ActiveTab } from '../ChatBot';

interface FluxbotDropdownProps {
  fluxBots: IFluxBotModel[];
  activeTab: ActiveTab;
  onClickSelectFluxBot: (item: IFluxBotModel) => void;
}

export function FluxbotDropdown({
  fluxBots,
  activeTab,
  onClickSelectFluxBot,
}: Readonly<FluxbotDropdownProps>) {
  const { user } = useAuthStore(state => state);

  const [searchQuery, setSearchQuery] = useState('');

  const filteredBots = searchQuery
    ? fluxBots?.filter(bot =>
        bot.name?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : fluxBots;

  // Group the filtered bots by space and sort within each group
  const groupedBots = filteredBots?.reduce(
    (acc: { [key: string]: any[] }, bot) => {
      const space = bot.space || 'No Space';
      if (!acc[space]) {
        acc[space] = [];
      }
      acc[space].push(bot);
      // Sort the bots in this space alphabetically (case-insensitive)
      acc[space].sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      return acc;
    },
    {},
  );

  const sortedSpaces = Object.keys(groupedBots).sort((a, b) => {
    if (a.toLowerCase() === 'personal space') return -1;
    if (b.toLowerCase() === 'personal space') return 1;
    return a.localeCompare(b);
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '8px',
      }}
    >
      <Box>
        <input
          type="text"
          placeholder="Search FluxBots..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onClick={e => e.stopPropagation()}
          style={{
            width: '88%',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #D0D5DD',
            backgroundColor: user?.theme === 'dark' ? '#101828' : '#fff',
            color: user?.theme === 'dark' ? '#D0D5DD' : '#475467',
            outline: 'none',
          }}
        />
      </Box>

      {(!groupedBots || Object.keys(groupedBots).length === 0) && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography
            sx={{
              color: user?.theme === 'dark' ? '#D0D5DD' : '#475467',
              fontSize: '14px',
            }}
          >
            No FluxBots found
          </Typography>
        </Box>
      )}

      {
        sortedSpaces.map(space => (
          <Box key={space}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: user?.theme === 'dark' ? '#D0D5DD' : '#475467',
                padding: '8px 16px 4px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <FolderUser
                size={14}
                color={user?.theme === 'dark' ? '#D0D5DD' : '#475467'}
              />
              {space}
            </Typography>

            {groupedBots[space].map(item => (
              <MenuItem
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: activeTab?.id === item.id ? '#B589FF' : '',
                  paddingLeft: '24px',
                  marginLeft: '8px',
                  borderLeft: '2px solid transparent',
                  '&:hover': {
                    borderLeft: `2px solid ${
                      user?.theme === 'dark' ? '#D0D5DD' : '#B589FF'
                    }`,
                    backgroundColor:
                      activeTab?.id === item.id
                        ? '#B589FF'
                        : user?.theme === 'dark'
                        ? '#1D2939'
                        : '#F2F4F7',
                  },
                }}
                onClick={e => {
                  e.stopPropagation();
                  onClickSelectFluxBot(item);
                }}
              >
                <ChatCircleText
                  size={16}
                  color={
                    activeTab?.id === item.id
                      ? '#fff'
                      : user?.theme === 'dark'
                      ? '#D0D5DD'
                      : '#667085'
                  }
                />
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color:
                      activeTab?.id === item.id
                        ? '#fff'
                        : user?.theme === 'dark'
                        ? '#D0D5DD'
                        : '#475467',
                  }}
                >
                  {item.name}
                </Typography>
              </MenuItem>
            ))}
          </Box>
        ))
      }
    </Box>
  );
}
